import * as React from 'react';
import { connect } from 'react-redux';
import { isEqual, isNil } from 'lodash';
import classnames from 'classnames';

import {
  fetchMessages,
  fetchNewMessages,
  fetchPreviousMessages,
  reset,
  sendAttachment,
  sendMessage
} from 'modules/messenger/reducers/messages';
import { fetchOfferDeliverables } from 'modules/subcampaigns/reducers/offerDeliverables';
import { updateUnreadConversations } from '../reducers/conversations';

import Spinner from 'components/spinner/Spinner';
import MessagesActions from 'modules/messenger/components/messages-actions/MessagesActions';
import MessagesList from 'modules/messenger/components/MessagesList';
import MessengerMessagesHeader from 'modules/messenger/components/messenger-messages-header/MessengerMessagesHeader';
import MessengerSidePanels from 'modules/messenger/components/MessengerSidePanels';

import { t } from 'utils/language';
import { shouldMessengerBeDisabled } from 'utils/messages';
import { ORGANIZATION_PROFILE_TYPE_BRAND, ORGANIZATION_PROFILE_TYPE_INFLUENCER } from 'utils/organization';

import type {
  BrandProfile,
  Campaign,
  MessengerConversation,
  MessengerNotifications,
  MessengerSendMessage,
  OrganizationProfileType,
  PromiseAction,
  Subcampaign
} from 'types';

type OwnProps = {
  type: OrganizationProfileType,
  subcampaignId: number,
  selectedConversation?: MessengerConversation | null,
  variablePaymentsAmount: number
};

type StateProps = {
  isLoadingMessages: boolean,
  isLoadingSelectedConversation: boolean,
  hasUnreadFilter: boolean,
  campaign: Campaign,
  brandProfile: BrandProfile,
  subcampaign: Subcampaign | undefined | null,
  messengerNotifications: MessengerNotifications
};

type DispatchProps = {
  dispatchReset: () => void,
  dispatchFetchMessages: (subcampaign: Subcampaign) => Promise<void>,
  dispatchFetchPreviousMessages: (subcampaign: Subcampaign) => void,
  dispatchFetchNewMessages: (subcampaign: Subcampaign, type: OrganizationProfileType) => void,
  dispatchSendMessage: (subcampaign: Subcampaign, message: MessengerSendMessage) => PromiseAction,
  dispatchSendAttachment: (subcampaign: Subcampaign, message: MessengerSendMessage) => PromiseAction,
  dispatchFetchOfferDeliverables: (offerId: number) => void,
  dispatchUpdateUnreadConversations: (campaignId: number, externalId: string) => Promise<void>
};

type Props = OwnProps & StateProps & DispatchProps;

type State = {
  size: 'small' | 'normal'
};

class Messages extends React.PureComponent<Props, State> {
  state: State = {
    size: 'normal'
  };

  componentDidMount() {
    const {
      subcampaign,
      dispatchFetchOfferDeliverables
    } = this.props;

    if (subcampaign) {
      this.fetchMessages(subcampaign);
    }

    if (subcampaign && subcampaign.offer) {
      dispatchFetchOfferDeliverables(subcampaign.offer.id);
    }
  }

  componentDidUpdate(prevProps: Props) {
    const {
      subcampaign,
      dispatchFetchOfferDeliverables
    } = this.props;

    if (subcampaign && !isEqual(subcampaign, prevProps.subcampaign)) {
      this.fetchMessages(subcampaign);

      if (subcampaign.offer) {
        dispatchFetchOfferDeliverables(subcampaign.offer.id);
      }
    }
  }

  componentWillUnmount() {
    const { dispatchReset } = this.props;

    dispatchReset();
  }


  fetchMessages(subcampaign: Subcampaign): void {
    const {
      hasUnreadFilter,
      selectedConversation,
      messengerNotifications,
      dispatchFetchMessages,
      dispatchUpdateUnreadConversations
    } = this.props;

    const isUnreadConversation = selectedConversation?.externalId &&
        messengerNotifications.unreadChatsMap[selectedConversation?.externalId];

    dispatchFetchMessages(subcampaign)
      .then(() => {
        if (selectedConversation?.externalId && isUnreadConversation && hasUnreadFilter) {
          dispatchUpdateUnreadConversations(subcampaign.campaignId, selectedConversation.externalId);
        }
      });
  }

  getProfileId(): number | undefined | null {
    const { type, subcampaign } = this.props;

    let profileId;

    if (type === ORGANIZATION_PROFILE_TYPE_BRAND && subcampaign && subcampaign.campaign) {
      profileId = subcampaign.campaign.brandProfileId;
    } else if (type === ORGANIZATION_PROFILE_TYPE_INFLUENCER && subcampaign && subcampaign.creatorProfile) {
      profileId = subcampaign.creatorProfile.id;
    }

    return profileId;
  }

  handleLoadPreviousPage = (): void => {
    const { subcampaign, dispatchFetchPreviousMessages } = this.props;

    if (subcampaign) {
      dispatchFetchPreviousMessages(subcampaign);
    }
  };

  handleLoadNewMessages = (): void => {
    const { subcampaign, type, dispatchFetchNewMessages } = this.props;

    if (subcampaign) {
      dispatchFetchNewMessages(subcampaign, type);
    }
  };

  handleSendMessage = (message: string): PromiseAction => {
    const {
      subcampaign,
      type,
      dispatchSendMessage
    } = this.props;

    const profileId = this.getProfileId();

    if (subcampaign && !isNil(profileId)) {
      return dispatchSendMessage(subcampaign, {
        text: message,
        metadata: {
          profile: {
            type,
            id: profileId
          }
        }
      });
    }

    return Promise.reject(new Error('empty-context'));
  };

  handleSendAttachment = (file: File): PromiseAction => {
    const {
      subcampaign,
      type,
      dispatchSendAttachment
    } = this.props;

    const profileId = this.getProfileId();

    if (subcampaign && !isNil(profileId)) {
      return dispatchSendAttachment(subcampaign, {
        file,
        metadata: {
          profile: {
            type,
            id: profileId
          }
        }
      });
    }

    return Promise.reject(new Error('empty-context'));
  };

  handleSizeChangeRequest = (size: 'small' | 'normal') => this.setState({ size });

  render() {
    const {
      isLoadingMessages,
      isLoadingSelectedConversation,
      subcampaign,
      subcampaignId,
      selectedConversation,
      type,
      campaign,
      brandProfile,
      variablePaymentsAmount
    } = this.props;
    const { size } = this.state;

    const disableMessenger = selectedConversation ? shouldMessengerBeDisabled(selectedConversation.subcampaign) : false;

    const messagesClass = classnames({
      'messenger-messages': true,
      [`messenger-messages--size-${size}`]: true,
      'messenger-messages--full-width': type === ORGANIZATION_PROFILE_TYPE_INFLUENCER
    });

    return (
      <section className={messagesClass}>
        <MessengerMessagesHeader
          profileType={type}
          subcampaignId={subcampaignId}
          variablePaymentsAmount={variablePaymentsAmount}
        />
        {
          isNil(subcampaign) && (isLoadingSelectedConversation || isLoadingMessages) && (
            <Spinner
              color="light"
              size="small"
              position="inline"
            />
          )
        }
        {
          !isNil(subcampaign) ? (
            <div className="messenger-messages__body">
              <div className="messenger-messages__messages">
                {
                  disableMessenger ?
                    <div className="messenger-messages__message-lightweight">
                      <div className="messenger-messages__message-lightweight--text">
                        {t('messenger.lightweight_message')}
                      </div>
                    </div> :
                    <MessagesList
                      campaign={{
                        ...(type === ORGANIZATION_PROFILE_TYPE_BRAND ? campaign : subcampaign.campaign),
                        brandProfile
                      }}
                      isBrand={type === ORGANIZATION_PROFILE_TYPE_BRAND}
                      subcampaignId={subcampaignId}
                      subcampaign={subcampaign}
                      onLoadPreviousPage={this.handleLoadPreviousPage}
                      onLoadNewMessages={this.handleLoadNewMessages}
                    />
                }
                {
                  !isNil(subcampaignId) &&
                    <MessagesActions
                      profileId={this.getProfileId()}
                      subcampaignId={subcampaignId}
                      isMessengerDisabled={disableMessenger}
                      onAttachFiles={this.handleSendAttachment}
                      onSendMessage={this.handleSendMessage}
                    />
                }
              </div>
              {
                type === ORGANIZATION_PROFILE_TYPE_BRAND && (
                  <MessengerSidePanels
                    campaign={campaign}
                    subcampaign={subcampaign}
                    isMessengerDisabled={disableMessenger}
                    onSizeChangeRequest={this.handleSizeChangeRequest}
                  />
                )
              }
            </div>
          ) : (
            <div className="messenger-messages--empty">
              {t('messenger.no_messages')}
            </div>
          )
        }
      </section>
    );
  }
}

const mapStateToProps = (state, props): StateProps => {
  const stateToProps: StateProps = {
    subcampaign: null,
    campaign: state.campaignBrief.brief,
    isLoadingMessages: state.messages.isLoading,
    isLoadingSelectedConversation: false,
    brandProfile: state.brandProfile.profile,
    messengerNotifications: state.messengerNotifications,
    hasUnreadFilter: state.conversations.hasUnreadFilter
  };

  if (props.type === ORGANIZATION_PROFILE_TYPE_INFLUENCER) {
    stateToProps.subcampaign = state.subcampaign.data;
  } else {
    stateToProps.isLoadingSelectedConversation = state.conversations.isSelectedConversationLoading;

    const conversation: MessengerConversation | undefined | null = state.conversations.selectedConversation;
    if (conversation) {
      stateToProps.subcampaign = conversation.subcampaign;
    }
  }

  return stateToProps;
};

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchReset: () => {
    dispatch(reset());
  },

  dispatchFetchMessages: (subcampaign: Subcampaign) => dispatch(fetchMessages(subcampaign)),

  dispatchFetchPreviousMessages: (subcampaign: Subcampaign) => {
    dispatch(fetchPreviousMessages(subcampaign));
  },

  dispatchFetchNewMessages: (subcampaign: Subcampaign, type: OrganizationProfileType) => {
    dispatch(fetchNewMessages(subcampaign, type));
  },

  dispatchSendMessage: (subcampaign: Subcampaign, message: MessengerSendMessage) => dispatch(sendMessage(subcampaign, message)),
  dispatchSendAttachment: (subcampaign: Subcampaign, message: MessengerSendMessage) => dispatch(sendAttachment(subcampaign, message)),
  dispatchFetchOfferDeliverables: (offerId: number) => dispatch(fetchOfferDeliverables(offerId, false)),
  dispatchUpdateUnreadConversations: (campaignId: number, externalId: string) => dispatch(updateUnreadConversations(campaignId, externalId))
});

export default connect(mapStateToProps, mapDispatchToProps)(Messages);
